import type { MeetingTypePayloadData } from '../types';

import { useTranslation } from 'react-i18next';
import { Flex, Typography, Form, Divider } from 'antd';

import themeToken from '@lib/theme/tokens/index';
import { StyledInput } from '@/lib/theme/components/Input';
import { StyledButton } from '@/lib/theme/components/Button';
import { StyledForm } from '@/components/Shared/Form';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import CustomizeRequiredMark from '@/components/Shared/CustomizeRequiredMark';
import { useTrackEvent } from '@/hooks/useTrackEvents';

import { useAddMeetingType } from '../api/meetings/addMeetingType';

const { Title } = Typography;

type Props = {
  handleCancel?: () => void;
  handleOk?: () => void;
};

const MeetingTypeForm = ({ handleCancel, handleOk }: Props) => {
  const { sendMixpanelEvent } = useTrackEvent({ page: 'add-meeting-type-modal' });
  const { t } = useTranslation('investorRelations', {
    keyPrefix: 'meetings',
  });

  const { selectedCompany } = useSelectedCompanyStore((state) => state);

  const { mutate: mutateAddMeetingType, isPending: isAddMeetingTypePending } = useAddMeetingType({
    postSuccess: () => {
      handleOk?.();
    },
  });

  const [form] = Form.useForm();

  const cancelClickHandler = () => {
    sendMixpanelEvent('back-btn-clicked');
    form.resetFields();
    handleCancel?.();
  };

  const onFinish = (values: MeetingTypePayloadData) => {
    sendMixpanelEvent('submit-btn-clicked');
    mutateAddMeetingType({
      companyID: selectedCompany?.cid as number,
      body: {
        name: values.name,
        notice_days: values.notice_days,
      },
    });
  };

  return (
    <StyledForm
      layout="vertical"
      form={form}
      onFinish={onFinish}
      size="large"
      requiredMark={CustomizeRequiredMark}
      disabled={isAddMeetingTypePending}
    >
      <Flex align="start" vertical>
        <Title
          level={5}
          style={{
            color: themeToken['branding-primary-6'],
          }}
        >
          {t('meetingType')}
        </Title>
        <Divider
          style={{
            marginBlock: themeToken.margin,
            borderColor: themeToken['branding-natural-3'],
          }}
        />

        <Flex gap={24} style={{ width: '100%' }} justify="space-between">
          <Form.Item label={t('name')} name="name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <StyledInput placeholder={t('enterName')} />
          </Form.Item>
          {/* <Form.Item label={t('noticeDays')} name="notice_days">
            <StyledInput placeholder={t('enterDays')} />
          </Form.Item> */}
        </Flex>

        <Flex style={{ width: '100%' }} justify="space-between">
          <StyledButton ghost type="primary" onClick={cancelClickHandler} size="middle">
            {t('back')}
          </StyledButton>
          <StyledButton loading={isAddMeetingTypePending} type="primary" htmlType="submit" size="middle">
            {t('save')}
          </StyledButton>
        </Flex>
      </Flex>
    </StyledForm>
  );
};

export default MeetingTypeForm;
